import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from 'state/axios';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as yup from 'yup';
import { 
    Box, 
    Typography,
    TextField, 
    Button,  
    CircularProgress, 
    FormControlLabel, 
    Checkbox, 
    MenuItem,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';


const RankPage = () => {
    useAuthCheck();
    const { rankId } = useParams();    
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    const [formValues, setFormValues] = useState({
        rankCode: '',
        description: '',
        rankType: '',
        rankPriority: 0,
        isActive: true,
    });


    useEffect(() => {     
        const fetchRank = async () => {
            try {
                setLoading(true);

                if (rankId) {
                    const res = await api.get(`/ranks/${rankId}`);

                    setFormValues({
                        rankCode: res.data.rankCode || '',
                        description: res.data.description || '',
                        rankType: res.data.rankType || '',
                        rankPriority: res.data.rankPriority || 99,
                        isActive: res.data.isActive ?? true,
                    });
                }
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };

        fetchRank();
    }, [rankId]);
    
    const checkoutSchema = yup.object().shape({
        rankCode: yup.string().required('Required'),
    });

    const handleFormSubmit = async (values, onSubmitProps) => {
        try {
            const method = rankId ? "put" : "post";
            const url = rankId 
                ? `/ranks/update/${rankId}` 
                : '/ranks/new';

            await api({
                method,
                url,
                data: {
                    rankCode: values.rankCode,
                    description: values.description,
                    rankType: values.rankType,
                    rankPriority: values.rankPriority,
                    isActive: values.isActive,
                },
            });

            toast.success(rankId 
                ? "Rank updated successfully" 
                : "Rank created successfully"
            );

            // onSubmitProps.resetForm(); 
            navigate("/ranks");
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to submit form!");
            setError(error);
        }
    };

    
    return (
        <Box className="main-container-box">                
            <FlexBetween>
                <Header
                    title="Rank"
                    subtitle={`${loading ? 'Loading...' : rankId ? 'Update' : 'Create'}`}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <StyledButton 
                            onClick={() => navigate("/ranks")}
                        >
                            <ArrowBackIosNewIcon />
                        </StyledButton>
                    </Box>
                )}
            </FlexBetween>
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </FlexCenter>
            ) : (
                <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                    <Formik
                        initialValues={formValues}
                        enableReinitialize={true}
                        validationSchema={checkoutSchema}
                        onSubmit={handleFormSubmit}
                    >
                        {({
                            values,
                            errors,
                            touched,
                            handleBlur,
                            handleChange,
                            handleSubmit,
                        }) => (
                            <form onSubmit={handleSubmit}>
                                <Box
                                    display="grid"
                                    gap={2}
                                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                >
                                    <TextField
                                        fullWidth
                                        required
                                        variant="filled"
                                        type="text"
                                        label="Rank Code"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.rankCode}
                                        name="rankCode"
                                        error={!!touched.rankCode && !!errors.rankCode}
                                        helperText={touched.rankCode && errors.rankCode}
                                        sx={{ gridColumn: 'span 12' }}
                                    />

                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        multiline
                                        rows={2}
                                        label="Description"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.description}
                                        name="description"
                                        error={!!touched.description && !!errors.description}
                                        helperText={touched.description && errors.description}
                                        sx={{ gridColumn: 'span 12' }}
                                    />
                                    
                                    <TextField
                                        select
                                        label="Rank Type"
                                        variant="filled"
                                        value={values.rankType}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        name="rankType"
                                        helperText={touched.rankType && errors.rankType}
                                        error={!!touched.rankType && !!errors.rankType}
                                        sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                                    >
                                        <MenuItem value="Flight Crew">Flight Crew</MenuItem>
                                        <MenuItem value="Cabin Crew">Cabin Crew</MenuItem>
                                        <MenuItem value="Officer">Officer</MenuItem>                                            
                                        <MenuItem value="Other">Other</MenuItem>                                            
                                    </TextField>
                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        type="number"
                                        label="Rank Priority"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.rankPriority}
                                        name="rankPriority"
                                        error={!!touched.rankPriority && !!errors.rankPriority}
                                        helperText={touched.rankPriority && errors.rankPriority}
                                        slotProps={{
                                            inputProps: { min: 0, style: { textAlign: 'right' } } 
                                        }}
                                        sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                                    />

                                    <FormControlLabel
                                        label="Is Active"
                                        control={
                                            <Checkbox
                                            checked={values.isActive}
                                            onChange={handleChange}
                                            name="isActive"
                                            color="primary"
                                            />
                                        }
                                        sx={{ gridColumn: 'span 12' }}
                                    />
                                    
                                    <Button
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                        color="secondary"
                                        sx={{ gridColumn: "span 12", height: 42 }}
                                    >
                                        {rankId ? "Update" : "Create"}
                                    </Button>                                    
                                </Box>
                            </form>
                        )}
                    </Formik>
                </Box>
            )}
        </Box>
    );
};

export default RankPage;
